import React, { useState, useEffect } from 'react';
import { Script, scriptApi } from '../services/api';
import { X, GitBranch, Clock, User, RotateCcw, Eye, FileText } from 'lucide-react';
import { CodeDiffViewer } from './CodeDiffViewer';
import './ScriptVersionHistory.css';

interface ScriptVersionHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  currentScript: Script;
  onVersionLoad: (script: Script) => void;
}

interface VersionNode {
  id: string;
  version: number;
  parent_id: string | null;
  created_at: string | null;
  metadata: any;
  children: string[];
}

export const ScriptVersionHistory: React.FC<ScriptVersionHistoryProps> = ({
  isOpen,
  onClose,
  currentScript,
  onVersionLoad
}) => {
  const [versionTree, setVersionTree] = useState<{
    root: string | null;
    nodes: Record<string, VersionNode>;
    current_id: string;
  } | null>(null);
  const [versions, setVersions] = useState<Script[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<Script | null>(null);
  const [compareMode, setCompareMode] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);

  useEffect(() => {
    if (isOpen && currentScript.script_id) {
      loadVersionData();
    }
  }, [isOpen, currentScript.script_id]);

  const loadVersionData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load both version list and tree
      const [versionsRes, treeRes] = await Promise.all([
        scriptApi.getVersions(currentScript.script_id),
        scriptApi.getVersionTree(currentScript.script_id)
      ]);

      setVersions(versionsRes.data);
      setVersionTree(treeRes.data);
      
      // Select current version by default
      const current = versionsRes.data.find(v => v.script_id === currentScript.script_id);
      if (current) {
        setSelectedVersion(current);
      }
    } catch (err) {
      console.error('Failed to load version data:', err);
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleVersionSelect = async (version: Script) => {
    setSelectedVersion(version);
    
    if (compareMode) {
      setShowDiff(true);
    }
  };

  const handleLoadVersion = async () => {
    if (!selectedVersion) return;

    try {
      const response = await scriptApi.get(selectedVersion.script_id);
      onVersionLoad(response.data);
      onClose();
    } catch (err) {
      console.error('Failed to load version:', err);
      setError('Failed to load selected version');
    }
  };

  const handleRollback = async () => {
    if (!selectedVersion) return;

    const reason = prompt('Please provide a reason for this rollback:');
    if (!reason) return;

    setRollbackLoading(true);
    try {
      const response = await scriptApi.rollbackToVersion(selectedVersion.script_id, reason);
      if (response.data.success) {
        // Load the new rolled-back version
        const newScriptRes = await scriptApi.get(response.data.new_script_id);
        onVersionLoad(newScriptRes.data);
        onClose();
      }
    } catch (err) {
      console.error('Failed to rollback:', err);
      setError('Failed to create rollback version');
    } finally {
      setRollbackLoading(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getVersionLabel = (version: Script) => {
    const metadata = version.metadata || {};
    
    if (metadata.action === 'rollback') {
      return `Rollback from v${metadata.rollback_from_version} to v${metadata.rollback_to_version}`;
    } else if (metadata.fix_type === 'validation_fixes') {
      return `Validation Fixes (${metadata.fixes_applied || 0} fixes)`;
    } else if (version.parent_script_id) {
      return 'Script Revision';
    } else {
      return 'Original Script';
    }
  };

  const renderVersionTimeline = () => {
    if (!versions.length) return null;

    return (
      <div className="version-timeline">
        {versions.map((version, index) => (
          <div
            key={version.script_id}
            className={`timeline-item ${version.script_id === currentScript.script_id ? 'current' : ''} ${version.script_id === selectedVersion?.script_id ? 'selected' : ''}`}
            onClick={() => handleVersionSelect(version)}
          >
            <div className="timeline-marker">
              <div className="timeline-dot" />
              {index < versions.length - 1 && <div className="timeline-line" />}
            </div>
            
            <div className="timeline-content">
              <div className="timeline-header">
                <span className="timeline-version">v{version.version || 1}</span>
                {version.script_id === currentScript.script_id && (
                  <span className="current-indicator">Current</span>
                )}
              </div>
              
              <div className="timeline-label">
                {getVersionLabel(version)}
              </div>
              
              <div className="timeline-meta">
                <Clock size={12} />
                <span>{formatDate(version.created_at)}</span>
              </div>

              {version.metadata?.reason && (
                <div className="timeline-reason">
                  "{version.metadata.reason}"
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="version-history-overlay" onClick={onClose}>
      <div className="version-history-modal" onClick={e => e.stopPropagation()}>
        <div className="version-history-header">
          <div className="header-title">
            <GitBranch size={20} />
            <h2>Version History</h2>
            <span className="version-count">{versions.length} versions</span>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="version-history-error">
            {error}
          </div>
        )}

        <div className="version-history-body">
          {loading ? (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading version history...</p>
            </div>
          ) : showDiff && selectedVersion ? (
            <div className="diff-view">
              <div className="diff-header">
                <h3>Comparing v{currentScript.version || 1} → v{selectedVersion.version || 1}</h3>
                <button onClick={() => setShowDiff(false)}>Back to Timeline</button>
              </div>
              <CodeDiffViewer
                originalCode={currentScript.code}
                adaptedCode={selectedVersion.code}
                changes={[]}
                preservationRatio={0}
                visualDiff={undefined}
              />
            </div>
          ) : (
            <>
              <div className="version-history-content">
                <div className="timeline-section">
                  <h3>Version Timeline</h3>
                  {renderVersionTimeline()}
                </div>

                {selectedVersion && (
                  <div className="version-details">
                    <h3>Selected Version Details</h3>
                    <div className="details-card">
                      <div className="detail-row">
                        <span className="detail-label">Version:</span>
                        <span className="detail-value">v{selectedVersion.version || 1}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Created:</span>
                        <span className="detail-value">{formatDate(selectedVersion.created_at)}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Type:</span>
                        <span className="detail-value">{getVersionLabel(selectedVersion)}</span>
                      </div>
                      {selectedVersion.metadata?.reason && (
                        <div className="detail-row">
                          <span className="detail-label">Reason:</span>
                          <span className="detail-value">{selectedVersion.metadata.reason}</span>
                        </div>
                      )}
                      {selectedVersion.metadata?.fixes_applied && (
                        <div className="detail-row">
                          <span className="detail-label">Fixes Applied:</span>
                          <span className="detail-value">{selectedVersion.metadata.fixes_applied}</span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span className="detail-label">Script ID:</span>
                        <span className="detail-value mono">{selectedVersion.script_id}</span>
                      </div>
                    </div>

                    <div className="version-actions">
                      <button
                        className="action-btn view-btn"
                        onClick={() => setShowDiff(true)}
                        disabled={selectedVersion.script_id === currentScript.script_id}
                      >
                        <Eye size={16} />
                        Compare
                      </button>
                      
                      <button
                        className="action-btn load-btn"
                        onClick={handleLoadVersion}
                        disabled={selectedVersion.script_id === currentScript.script_id}
                      >
                        <FileText size={16} />
                        View
                      </button>
                      
                      <button
                        className="action-btn rollback-btn"
                        onClick={handleRollback}
                        disabled={selectedVersion.script_id === currentScript.script_id || rollbackLoading}
                      >
                        <RotateCcw size={16} />
                        {rollbackLoading ? 'Restoring...' : 'Restore'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};